/**
 * ROU Disclosure table — "Leases, if any" and "Deposits, if any" columns from
 * lease testing / modification / reassessment outputs and Execution_OFA_SDOut.json.
 */

import {
  computeJournalEntry,
  findLeaseByName,
} from "@/utils/leaseReassessmentJournalEntry";
import {
  computeModificationJournalEntry,
  type ModificationJournalFlags,
  signedRouAdjustmentFromModificationJournal,
} from "@/utils/leaseModificationJournalEntry";
import {
  indianFyMarchBounds,
  monthLabelInRange,
  parseMmDdYyyy,
  parseMonthLabel,
} from "@/utils/leaseDisclosureCompute";

function num(v: unknown): number {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v))) return Number(v);
  return 0;
}

export function parseRepDateDdMmYyyy(s: string): Date | null {
  const m = /^(\d{1,2})-(\d{1,2})-(\d{4})$/.exec(String(s || "").trim());
  if (!m) return null;
  const d = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10) - 1;
  const y = parseInt(m[3], 10);
  const dt = new Date(y, mo, d);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function commencementDate(lease: Record<string, unknown> | null): Date | null {
  if (!lease) return null;
  const li = lease.lease_info;
  if (!li || typeof li !== "object") return null;
  const s = String((li as Record<string, unknown>)["Lease Commencement Date"] ?? "").trim();
  return parseMmDdYyyy(s);
}

function eventCommencementMonth(lease: Record<string, unknown> | null): Date | null {
  const d = commencementDate(lease);
  if (!d) return null;
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function earliestMonthFromMap(monthMap: Record<string, Record<string, unknown>>): Date | null {
  let best: Date | null = null;
  for (const label of Object.keys(monthMap)) {
    const d = parseMonthLabel(label);
    if (!d) continue;
    if (!best || d.getTime() < best.getTime()) best = d;
  }
  return best;
}

function firstRowRouOrOpening(lease: Record<string, unknown> | null): number {
  if (!lease) return 0;
  const sch = lease.schedule;
  if (!Array.isArray(sch) || sch.length === 0) return 0;
  const row = sch[0] as Record<string, unknown>;
  const rou = num(row["ROU"]);
  if (rou !== 0) return rou;
  return num(row["Opening Balance"]);
}

function leaseNamesFromAssessmentOnly(assessmentLeases: unknown): string[] {
  const set = new Set<string>();
  if (!Array.isArray(assessmentLeases)) return [];
  for (const L of assessmentLeases) {
    if (!L || typeof L !== "object") continue;
    const li = (L as Record<string, unknown>).lease_info;
    if (li && typeof li === "object") {
      const nm = String((li as Record<string, unknown>)["Name of Lease"] ?? "").trim();
      if (nm) set.add(nm);
    }
  }
  return [...set].sort();
}

/** Same month/source blending as lease disclosure Accretion of Interest; field summed is Depreciation. */
type LeaseDepreciationSumMode = "current_fy" | "before_current_fy";

function sumLeaseDepreciationStream(
  reportingDate: Date,
  assessmentOut: unknown,
  modificationOut: unknown,
  reassessmentOut: unknown,
  mode: LeaseDepreciationSumMode,
): number {
  const { cyStart, cyEnd } = indianFyMarchBounds(reportingDate);
  const assessLeases = (assessmentOut as Record<string, unknown>)?.leases;
  const names = leaseNamesFromAssessmentOnly(assessLeases);
  const repMonthDate = new Date(reportingDate.getFullYear(), reportingDate.getMonth(), 1);

  let total = 0;

  for (const name of names) {
    const aLease = findLeaseByName(assessLeases, name);
    const mLease = findLeaseByName((modificationOut as Record<string, unknown>)?.leases, name);
    const rLease = findLeaseByName((reassessmentOut as Record<string, unknown>)?.leases, name);

    const aMap: Record<string, Record<string, unknown>> = {};
    const mMap: Record<string, Record<string, unknown>> = {};
    const rMap: Record<string, Record<string, unknown>> = {};

    if (Array.isArray(aLease?.schedule)) {
      for (const row of aLease!.schedule as Record<string, unknown>[]) {
        const ml = String(row["Month"] ?? "").trim();
        if (ml) aMap[ml] = row;
      }
    }
    if (Array.isArray(mLease?.schedule)) {
      for (const row of (mLease!.schedule as Record<string, unknown>[])) {
        const ml = String(row["Month"] ?? "").trim();
        if (ml) mMap[ml] = row;
      }
    }
    if (Array.isArray(rLease?.schedule)) {
      for (const row of (rLease!.schedule as Record<string, unknown>[])) {
        const ml = String(row["Month"] ?? "").trim();
        if (ml) rMap[ml] = row;
      }
    }

    const mStartDate = eventCommencementMonth(mLease) ?? earliestMonthFromMap(mMap);
    const rStartDate = eventCommencementMonth(rLease) ?? earliestMonthFromMap(rMap);
    let startDate: Date | null = null;
    let chosenMap: typeof aMap = aMap;
    if (mStartDate && rStartDate) {
      if (mStartDate.getTime() <= rStartDate.getTime()) {
        startDate = mStartDate;
        chosenMap = mMap;
      } else {
        startDate = rStartDate;
        chosenMap = rMap;
      }
    } else if (mStartDate) {
      startDate = mStartDate;
      chosenMap = mMap;
    } else if (rStartDate) {
      startDate = rStartDate;
      chosenMap = rMap;
    }

    const monthLabels = new Set<string>([...Object.keys(aMap), ...Object.keys(mMap), ...Object.keys(rMap)]);
    const sorted = [...monthLabels].sort((x, y) => {
      const dx = parseMonthLabel(x);
      const dy = parseMonthLabel(y);
      return (dx?.getTime() ?? 0) - (dy?.getTime() ?? 0);
    });

    for (const ml of sorted) {
      const d = parseMonthLabel(ml);
      if (!d) continue;
      if (d.getTime() > repMonthDate.getTime()) continue;

      let row: Record<string, unknown> | undefined;
      if (startDate && d.getTime() < startDate.getTime()) {
        row = aMap[ml];
      } else if (startDate) {
        row = chosenMap[ml];
      } else {
        row = aMap[ml];
      }

      if (!row) continue;
      const dep = num(row["Depreciation"]);

      if (mode === "current_fy") {
        if (monthLabelInRange(ml, cyStart, cyEnd)) total += dep;
      } else {
        if (d.getTime() < cyStart.getTime()) total += dep;
      }
    }
  }

  return total;
}

function parseDepositPaymentDate(raw: unknown): Date | null {
  const s = String(raw ?? "").trim();
  if (!s) return null;
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (iso) {
    const y = parseInt(iso[1], 10);
    const m = parseInt(iso[2], 10) - 1;
    const d = parseInt(iso[3], 10);
    const dt = new Date(y, m, d);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }
  return parseRepDateDdMmYyyy(s);
}

function parseSdPeriodMonth(raw: unknown): Date | null {
  const s = String(raw ?? "").trim();
  if (!s) return null;
  return parseMonthLabel(s);
}

function modificationFlagsFromInputRow(row: Record<string, unknown>): ModificationJournalFlags {
  const yn = (v: unknown): "Yes" | "No" | "" => {
    const s = String(v ?? "").trim();
    if (s === "Yes" || s === "No") return s;
    return "";
  };
  return {
    scope_reduced: yn(row.scope_reduced),
    standalone_selling_price: yn(row.standalone_selling_price),
    lease_period_increased: yn(row.lease_period_increased),
    other_reason: String(row.other_reason ?? ""),
    original_number_of_assets: Number(row.original_number_of_assets) || 0,
    revised_number_of_assets: Number(row.revised_number_of_assets) || 0,
  };
}

/**
 * ROU adjustment for lease modification / reassessment: sum of journal ROU lines (Lease Liability – Ind AS)
 * for all mod + reass input rows with event month in the current FY. Same helpers as substantive journals;
 * P&L excluded; signs preserved (negative stays negative).
 */
function sumLeaseGbTransfersFromIndAsJournals(
  reportingDate: Date,
  assessmentOut: unknown,
  modificationOut: unknown,
  reassessmentOut: unknown,
  modificationInp: unknown,
  reassessmentInp: unknown,
): number {
  const { cyStart, cyEnd } = indianFyMarchBounds(reportingDate);
  let total = 0;

  const modRows = (modificationInp as Record<string, unknown>)?.rows;
  if (Array.isArray(modRows)) {
    for (const raw of modRows) {
      if (!raw || typeof raw !== "object") continue;
      const row = raw as Record<string, unknown>;
      const name = String(row.lease_name ?? "").trim();
      if (!name) continue;
      const flags = modificationFlagsFromInputRow(row);
      const j = computeModificationJournalEntry(name, flags, assessmentOut, modificationOut);
      if (!j.ok) continue;
      if (!monthLabelInRange(j.eventMonth, cyStart, cyEnd)) continue;
      const sr = signedRouAdjustmentFromModificationJournal(j);
      if (sr !== null) total += sr;
    }
  }

  const reassRows = (reassessmentInp as Record<string, unknown>)?.rows;
  if (Array.isArray(reassRows)) {
    for (const raw of reassRows) {
      if (!raw || typeof raw !== "object") continue;
      const row = raw as Record<string, unknown>;
      const name = String(row.lease_name ?? "").trim();
      if (!name) continue;
      const j = computeJournalEntry(name, assessmentOut, reassessmentOut, modificationOut);
      if (!j.ok) continue;
      if (!monthLabelInRange(j.eventMonth, cyStart, cyEnd)) continue;
      total += Math.round(j.delta);
    }
  }

  return total;
}

export interface RouLeaseDepositColumnFigures {
  /** Gross block — opening (prior FY / prior commencement rules) */
  gbOpening: number;
  gbAdditions: number;
  gbTransfers: number;
  adOpening: number;
  adDepreciationCy: number;
  adEliminations: number;
}

export function computeRouLeaseDepositColumns(
  reportingDate: Date,
  assessmentOut: unknown,
  modificationOut: unknown,
  reassessmentOut: unknown,
  sdOut: unknown,
  modificationInp?: unknown,
  reassessmentInp?: unknown,
): { leases: RouLeaseDepositColumnFigures; deposits: RouLeaseDepositColumnFigures } {
  const { cyStart, cyEnd } = indianFyMarchBounds(reportingDate);
  const assessLeases = (assessmentOut as Record<string, unknown>)?.leases;
  const names = leaseNamesFromAssessmentOnly(assessLeases);

  let leaseGbOpening = 0;
  let leaseGbAdd = 0;
  const leaseGbTransfers = sumLeaseGbTransfersFromIndAsJournals(
    reportingDate,
    assessmentOut,
    modificationOut,
    reassessmentOut,
    modificationInp,
    reassessmentInp,
  );

  for (const name of names) {
    const aLease = findLeaseByName(assessLeases, name);
    const mLease = findLeaseByName((modificationOut as Record<string, unknown>)?.leases, name);
    const rLease = findLeaseByName((reassessmentOut as Record<string, unknown>)?.leases, name);

    type Pick = { lease: Record<string, unknown>; comm: Date };
    const picks: Pick[] = [];
    for (const L of [aLease, mLease, rLease]) {
      if (!L) continue;
      const c = commencementDate(L);
      if (c && c < cyStart) picks.push({ lease: L, comm: c });
    }
    if (picks.length > 0) {
      picks.sort((x, y) => y.comm.getTime() - x.comm.getTime());
      leaseGbOpening += firstRowRouOrOpening(picks[0].lease);
    }

    if (aLease) {
      const c = commencementDate(aLease);
      if (c && c >= cyStart && c <= cyEnd) {
        leaseGbAdd += firstRowRouOrOpening(aLease);
      }
    }
  }

  const leaseAdOpening = sumLeaseDepreciationStream(
    reportingDate,
    assessmentOut,
    modificationOut,
    reassessmentOut,
    "before_current_fy",
  );
  const leaseAdCy = sumLeaseDepreciationStream(
    reportingDate,
    assessmentOut,
    modificationOut,
    reassessmentOut,
    "current_fy",
  );

  const deposits: RouLeaseDepositColumnFigures = {
    gbOpening: 0,
    gbAdditions: 0,
    gbTransfers: 0,
    adOpening: 0,
    adDepreciationCy: 0,
    adEliminations: 0,
  };

  const root = sdOut as Record<string, unknown> | null;
  const data = root?.data;
  const items: unknown[] = Array.isArray(data) ? data : [];

  for (const item of items) {
    if (!item || typeof item !== "object") continue;
    const dep = item as Record<string, unknown>;
    const payDate = parseDepositPaymentDate(dep.deposit_payment_date);
    const sch = dep.schedule;
    if (!Array.isArray(sch) || sch.length === 0) continue;

    const first = sch[0] as Record<string, unknown>;
    const sl = first.sl_no ?? first["Sl No."];
    const rou0 = num(first.rou ?? first["ROU"]);
    const slIs1 = num(sl) === 1 || String(sl).trim() === "1";
    const treatAsFirstRow = slIs1 || sl === undefined || sl === null || String(sl).trim() === "";

    if (payDate && payDate < cyStart && treatAsFirstRow) {
      deposits.gbOpening += rou0;
    } else if (payDate && payDate >= cyStart && payDate <= cyEnd && treatAsFirstRow) {
      deposits.gbAdditions += rou0;
    }

    for (const raw of sch) {
      if (!raw || typeof raw !== "object") continue;
      const row = raw as Record<string, unknown>;
      const period = row.period ?? row["Month"];
      const d = parseSdPeriodMonth(period);
      if (!d) continue;
      const depAmt = num(row.depreciation ?? row["Depreciation"]);
      if (d < cyStart) deposits.adOpening += depAmt;
      else if (d >= cyStart && d <= cyEnd) deposits.adDepreciationCy += depAmt;
    }
  }

  return {
    leases: {
      gbOpening: Math.round(leaseGbOpening),
      gbAdditions: Math.round(leaseGbAdd),
      gbTransfers: Math.round(leaseGbTransfers),
      adOpening: Math.round(leaseAdOpening),
      adDepreciationCy: Math.round(leaseAdCy),
      adEliminations: 0,
    },
    deposits,
  };
}
